import { LlmInference, LlmImageInput } from "@mediapipe/tasks-genai";

export type ModelFamily = "gemma3" | "gemma4" | "qwen";

export type ChatRole = "system" | "user" | "assistant";

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

// Adapter shape both runtimes (MediaPipe / transformers.js) implement.
export interface LLMRuntime {
  readonly family: ModelFamily;
  isBusy(): boolean;
  supportsVision(): boolean;
  chat(messages: ChatMessage[], maxTokens: number): Promise<string>;
  chatWithImage(
    messages: ChatMessage[],
    image: LlmImageInput,
    maxTokens: number,
    stopWords?: string[],
  ): Promise<string>;
}

export class LLMWrapper {
  // Sticky disable for image inference once the underlying runtime errors.
  private visionDisabled: boolean = false;

  constructor(private readonly runtime: LLMRuntime) {}

  getFamily(): ModelFamily {
    return this.runtime.family;
  }

  isBusy(): boolean {
    return this.runtime.isBusy();
  }

  isVisionDisabled(): boolean {
    return this.visionDisabled;
  }

  supportsVision(): boolean {
    return !this.visionDisabled && this.runtime.supportsVision();
  }

  async chat(messages: ChatMessage[], maxTokens: number = 1024): Promise<string> {
    return this.runtime.chat(messages, maxTokens);
  }

  async askWithImage(
    prompt: string,
    image: LlmImageInput,
    maxTokens: number = 256,
    stopWords?: string[],
    timeoutMs: number = 12000,
  ): Promise<string> {
    if (!this.supportsVision()) {
      throw new Error(`Vision not supported (family=${this.runtime.family})`);
    }
    const messages: ChatMessage[] = [{ role: "user", content: prompt }];
    const inferenceP = this.runtime.chatWithImage(
      messages,
      image,
      maxTokens,
      stopWords,
    );
    const timeoutP = new Promise<string>((_, reject) =>
      setTimeout(
        () => reject(new Error(`askWithImage timed out after ${timeoutMs}ms`)),
        timeoutMs,
      ),
    );
    try {
      return await Promise.race([inferenceP, timeoutP]);
    } catch (err) {
      this.visionDisabled = true;
      console.warn(
        "[LLMWrapper] vision inference failed, disabling vision for this session:",
        err,
      );
      throw err;
    }
  }
}

// ---------- MediaPipe adapter (used for Qwen mobile and as a fallback) ----------

export class MediaPipeRuntime implements LLMRuntime {
  public readonly family: ModelFamily;
  private inflight: Promise<string> | null = null;

  constructor(
    private readonly llm: LlmInference,
    family: ModelFamily,
    private readonly visionEnabled: boolean,
  ) {
    this.family = family;
  }

  isBusy(): boolean {
    return this.inflight !== null;
  }

  supportsVision(): boolean {
    return this.visionEnabled && this.family === "gemma4";
  }

  async chat(messages: ChatMessage[], maxTokens: number): Promise<string> {
    const prompt = this.formatPrompt(messages);
    const response = await this.runInference(prompt, maxTokens);
    return response.trim();
  }

  async chatWithImage(
    messages: ChatMessage[],
    image: LlmImageInput,
    maxTokens: number,
    _stopWords?: string[],
  ): Promise<string> {
    if (!this.supportsVision()) {
      throw new Error(`Vision not enabled for ${this.family}`);
    }
    // MediaPipe runtime doesn't expose streaming early-stop; param is accepted for interface symmetry.
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    const promptText = lastUser?.content ?? "";
    // Gemma 4 chat template: <|turn>user\n...\n\n<|image|>\n\n...<turn|>\n<|turn>model\n
    const parts = [
      `<|turn>user\n${promptText}\n\n`,
      { imageSource: image },
      `\n\n<turn|>\n<|turn>model\n`,
    ];
    const response = await this.runInference(parts as any, maxTokens);
    return response.trim();
  }

  private async runInference(
    prompt: string | unknown[],
    maxTokens: number,
  ): Promise<string> {
    if (this.inflight) {
      try {
        await this.inflight;
      } catch {
        /* ignore — its caller already handled */
      }
    }
    const p = (this.llm.generateResponse as any)(prompt, maxTokens) as Promise<string>;
    this.inflight = p;
    // Hard cap so a stuck WASM call can't permanently wedge isBusy().
    const timeoutP = new Promise<string>((_, reject) =>
      setTimeout(
        () => reject(new Error("MediaPipe generateResponse timed out (10s)")),
        10000,
      ),
    );
    try {
      return await Promise.race([p, timeoutP]);
    } finally {
      // Clear inflight even on timeout — the underlying promise may keep
      // running on the GPU but at least new calls aren't blocked.
      if (this.inflight === p) this.inflight = null;
    }
  }

  private formatPrompt(messages: ChatMessage[]): string {
    switch (this.family) {
      case "qwen":
        return formatQwen(messages);
      case "gemma4":
        return formatGemma4(messages);
      case "gemma3":
        return formatGemma3(messages);
    }
  }
}

function formatGemma3(messages: ChatMessage[]): string {
  let out = "";
  let pendingSystem = "";
  for (const m of messages) {
    if (m.role === "system") {
      pendingSystem += (pendingSystem ? "\n\n" : "") + m.content;
      continue;
    }
    const role = m.role === "assistant" ? "model" : "user";
    let content = m.content;
    if (role === "user" && pendingSystem) {
      content = `${pendingSystem}\n\n${content}`;
      pendingSystem = "";
    }
    out += `<start_of_turn>${role}\n${content}\n<end_of_turn>\n`;
  }
  out += "<start_of_turn>model\n";
  return out;
}

function formatGemma4(messages: ChatMessage[]): string {
  let out = "";
  for (const m of messages) {
    const role = m.role === "assistant" ? "model" : m.role;
    out += `<|turn>${role}\n${m.content}\n<turn|>\n`;
  }
  out += "<|turn>model\n";
  return out;
}

function formatQwen(messages: ChatMessage[]): string {
  let out = "";
  for (const m of messages) {
    const role = m.role === "assistant" ? "assistant" : m.role;
    out += `<|im_start|>${role}\n${m.content}<|im_end|>\n`;
  }
  out += "<|im_start|>assistant\n";
  return out;
}
